// React and Next.js imports
import type { GetStaticProps, GetStaticPaths } from "next";

// Builder.io imports
import { builder } from "@builder.io/react";
import type { BasePageProps } from "@orlandohealth/build-kit";
import { GenericPage, GenericWrapper, buildServiceSchema, getLocalizedValue, resolvePageMetadataDescription } from "@orlandohealth/build-kit";

const Page = GenericPage.createPage(GenericWrapper);

export const getStaticProps: GetStaticProps<BasePageProps> = async ({
  params,
  locale,
  defaultLocale,
}: {
  params?: { url?: string[] };
  locale?: string;
  defaultLocale?: string;
}) => {
  const urlPath =
    "/services/" +
    (Array.isArray(params?.url) ? params.url.join("/") : params?.url || "");
  const result = await GenericPage.getStaticProps(
    urlPath,
    locale,
    defaultLocale,
    "page"
  );

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "";
  const pageUrl = `${siteUrl}${urlPath}`;
  const contentData = result.props?.content?.data;
  const builderLocale = locale || defaultLocale || "Default";
  const serviceName =
    getLocalizedValue(contentData?.title, builderLocale) ||
    getLocalizedValue(contentData?.metadata?.navigationTitle, builderLocale) ||
    "";
  const description =
    resolvePageMetadataDescription(contentData?.metadata, builderLocale) ||
    getLocalizedValue(contentData?.description, builderLocale) ||
    "";

  const enrichment = result.props?.schemaEnrichment;

  if (serviceName) {
    const { medicalWebPage, procedure } = buildServiceSchema({
      url: pageUrl,
      name: serviceName,
      ...(description && { description }),
      areaServed: [{ type: "State", name: "Florida" }],
      performedByOrgId: `${siteUrl}/#organization`,
    });

    return {
      ...result,
      props: {
        ...result.props,
        locale,
        defaultLocale,
        schemaEnrichment: {
          subOrganizations: enrichment?.subOrganizations || [],
          additionalEntities: [
            ...(enrichment?.additionalEntities || []),
            medicalWebPage,
            procedure,
          ],
        },
      },
    };
  }

  return {
    ...result,
    props: {
      ...result.props,
      locale,
      defaultLocale,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({
  locales = ["en_US"],
}) => {
  if (process.env.SKIP_SSG === "true") {
    return { paths: [], fallback: "blocking" };
  }
  const paths = [];

  for (const locale of locales) {
    const pages = await builder.getAll("page", {
      fields: "data.url",
      options: { noTargeting: true },
      locale: locale,
    });

    // Filter for service pages only
    const servicePaths = pages
      .map((page) => String(page.data?.url))
      .filter((url) => url?.startsWith("/services/") && url !== "/services")
      .map((url) => ({
        params: {
          url: url
            .replace(/^\/services\//, "")
            .split("/")
            .filter(Boolean),
        },
        locale,
      }));

    paths.push(...servicePaths);
  }

  return {
    paths,
    fallback: false,
  };
};

export default Page;
